/**
 * Plays video clip layers on the live editor canvas in step with the
 * timeline: playing during scene preview, otherwise paused on the frame for
 * the previewed time (the clip's first frame when not previewing).
 */

import { useEffect } from 'react';
import type { fabric } from 'fabric';
import { createLiveVideoLayers } from '@/lib/video/liveVideoLayers';
import { VIDEO_CLIP_CHANGED_EVENT } from '@/lib/video/videoEvents';
import { useVideoStore } from '@/stores/videoStore';

export function useLiveVideoLayers({ canvas, ready }: { canvas: fabric.Canvas | null; ready: boolean }): void {
  useEffect(() => {
    if (!canvas || !ready) return;
    const layers = createLiveVideoLayers(canvas);

    const sync = (): void => {
      const { playback, previewLocalMs, muted } = useVideoStore.getState();
      layers.sync({
        playing: playback === 'playing' && previewLocalMs !== null,
        localMs: previewLocalMs ?? 0,
        muted,
      });
    };
    const onClipChanged = (e: Event): void => {
      layers.refresh((e as CustomEvent<{ obj?: fabric.Object }>).detail?.obj ?? null);
    };

    const unsubscribe = useVideoStore.subscribe(sync);
    window.addEventListener(VIDEO_CLIP_CHANGED_EVENT, onClipChanged);
    sync();
    layers.scan();

    return () => {
      unsubscribe();
      window.removeEventListener(VIDEO_CLIP_CHANGED_EVENT, onClipChanged);
      layers.dispose();
    };
  }, [canvas, ready]);
}
